import { CalendarClock } from 'lucide-react';
import type { AppointmentResponseDto, Operatory } from '../domain/types';
import { EmptyState } from '@/shared/ui/EmptyState';
import { cn } from '@/shared/lib/utils';
import { AppointmentStatusPill } from './AppointmentStatusPill';
import {
  formatSlotDisplay,
  getSlotPeriod,
  PERIOD_LABELS,
} from './appointment-create-utils';

export interface DaySchedulePreview {
  startAt: string;
  endAt: string;
  durationMin: number;
  patientName?: string;
  isOverbooking?: boolean;
}

export interface DayAppointmentsPanelProps {
  dateLabel: string;
  appointments: AppointmentResponseDto[];
  timezone: string;
  operatories?: Operatory[];
  /** Dotted draft row for the window currently being booked. */
  preview?: DaySchedulePreview | null;
  className?: string;
}

type AgendaItem =
  | { kind: 'existing'; appt: AppointmentResponseDto }
  | { kind: 'preview'; preview: DaySchedulePreview };

/**
 * Fixed-height scrollable agenda of appointments already on the selected day,
 * with an optional dotted preview for the in-progress booking.
 */
export function DayAppointmentsPanel({
  dateLabel,
  appointments,
  timezone,
  operatories,
  preview,
  className,
}: DayAppointmentsPanelProps): JSX.Element {
  const items: AgendaItem[] = [
    ...appointments.map((appt) => ({ kind: 'existing' as const, appt })),
    ...(preview ? [{ kind: 'preview' as const, preview }] : []),
  ].sort((a, b) => {
    const aStart = a.kind === 'existing' ? a.appt.startAt : a.preview.startAt;
    const bStart = b.kind === 'existing' ? b.appt.startAt : b.preview.startAt;
    return new Date(aStart).getTime() - new Date(bStart).getTime();
  });

  const grouped = items.reduce<Record<string, AgendaItem[]>>((acc, item) => {
    const startAt = item.kind === 'existing' ? item.appt.startAt : item.preview.startAt;
    const p = getSlotPeriod(startAt, timezone);
    if (!acc[p]) acc[p] = [];
    acc[p].push(item);
    return acc;
  }, {});

  const periodOrder = ['morning', 'afternoon', 'evening'] as const;
  const countLabel = appointments.length + (preview ? 1 : 0);

  return (
    <div
      className={cn(
        'flex h-full min-h-[320px] max-h-[min(560px,70vh)] flex-col overflow-hidden rounded-xl border border-surface-100 bg-surface-50',
        className,
      )}
    >
      <div className="flex flex-none items-start justify-between gap-2 border-b border-surface-100 bg-white/70 px-4 py-3 backdrop-blur-sm">
        <div>
          <h4 className="text-[13px] font-semibold text-ink-900">
            Day schedule
          </h4>
          <p className="mt-0.5 text-[11px] text-ink-500">{dateLabel}</p>
        </div>
        <span className="rounded-full bg-surface-100 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-ink-600">
          {countLabel}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto nice-scroll px-3 py-3">
        {items.length === 0 ? (
          <EmptyState
            icon={CalendarClock}
            title="No appointments yet"
            description="Bookings for this day will appear here as a quick conflict check."
            cta={{ label: 'Book appointment', to: '/appointments/new' }}
            className="min-h-[180px] py-8"
          />
        ) : (
          <div className="space-y-4">
            {periodOrder.map((period) => {
              const periodItems = grouped[period];
              if (!periodItems?.length) return null;
              return (
                <div key={period}>
                  <div className="mb-2 flex items-center gap-2 px-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
                      {PERIOD_LABELS[period]}
                    </span>
                    <span className="h-px flex-1 bg-surface-200" />
                    <span className="text-[10px] font-medium tabular-nums text-ink-400">
                      {periodItems.length}
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {periodItems.map((item) => {
                      if (item.kind === 'preview') {
                        const { preview: p } = item;
                        return (
                          <li
                            key="draft-preview"
                            className={cn(
                              'rounded-lg border border-dashed bg-white/60 px-2.5 py-2',
                              p.isOverbooking
                                ? 'border-amber-400 bg-amber-50/50'
                                : 'border-brand-400 bg-brand-50/40',
                            )}
                          >
                            <div className="flex items-start gap-2">
                              <div
                                className={cn(
                                  'w-[52px] flex-none pt-0.5 text-[11px] font-semibold tabular-nums',
                                  p.isOverbooking ? 'text-amber-800' : 'text-brand-700',
                                )}
                              >
                                {formatSlotDisplay(p.startAt, timezone)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div
                                  className={cn(
                                    'truncate text-[12px] font-semibold',
                                    p.isOverbooking ? 'text-amber-900' : 'text-brand-800',
                                  )}
                                >
                                  {p.patientName ?? 'New booking'}
                                </div>
                                <div className="mt-0.5 truncate text-[10px] text-ink-500">
                                  {[
                                    `${p.durationMin} min`,
                                    'Draft',
                                    p.isOverbooking ? 'Overbook' : null,
                                  ].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                              <span
                                className={cn(
                                  'flex-none rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                                  p.isOverbooking
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-brand-100 text-brand-700',
                                )}
                              >
                                {p.isOverbooking ? 'Overbook' : 'Selected'}
                              </span>
                            </div>
                          </li>
                        );
                      }

                      const a = item.appt;
                      const chair = a.operatoryId
                        ? operatories?.find((o) => o.id === a.operatoryId)?.name
                        : undefined;
                      return (
                        <li
                          key={a.id}
                          className="rounded-lg border border-surface-100 bg-white px-2.5 py-2 shadow-soft"
                        >
                          <div className="flex items-start gap-2">
                            <div className="w-[52px] flex-none pt-0.5 text-[11px] font-semibold tabular-nums text-ink-900">
                              {formatSlotDisplay(a.startAt, timezone)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[12px] font-semibold text-ink-900">
                                {a.patientName ?? 'Patient'}
                              </div>
                              <div className="mt-0.5 truncate text-[10px] text-ink-500">
                                {[
                                  `${a.durationMin} min`,
                                  chair,
                                ].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                            <AppointmentStatusPill
                              status={a.status}
                              className="flex-none !text-[9px] !px-1.5 !py-0.5"
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
